/**
 * The columns an attendee CSV can carry, and the obvious mapping of a file's
 * headers onto them.
 *
 * The keys are the backend's column names (OPTIONAL_COLUMNS and the required
 * pair in functions/python/app/application/service/attendee_import.py) and
 * travel in the command's `columnMapping`. People never see them: the mapping
 * screen shows the label, and headers are matched on the label and aliases in
 * English and German, the way the agenda import matches its columns.
 */

import { squashHeader } from "./import-csv";

export type AttendeeColumnKey =
  | "firstName"
  | "lastName"
  | "email"
  | "jobTitle"
  | "company"
  | "trackName"
  | "attendeeType";

export interface AttendeeColumn {
  key: AttendeeColumnKey;
  label: string;
  required: boolean;
  /**
   * Header spellings mapped to this column without asking, compared with
   * accents, case, spaces and punctuation removed. The label and the key
   * always match too.
   */
  aliases: string[];
}

/** First and last name are required; the rest are optional. */
export const ATTENDEE_COLUMNS: AttendeeColumn[] = [
  {
    key: "firstName",
    label: "First name",
    required: true,
    aliases: ["first", "given name", "forename", "vorname"],
  },
  {
    key: "lastName",
    label: "Last name",
    required: true,
    aliases: ["last", "surname", "family name", "nachname", "familienname"],
  },
  {
    key: "email",
    label: "Email",
    required: false,
    aliases: [
      "e-mail",
      "mail",
      "email address",
      "e-mail address",
      "e-mail-adresse",
      "mailadresse",
    ],
  },
  {
    key: "jobTitle",
    label: "Job title",
    required: false,
    aliases: [
      "position",
      "jobtitel",
      "funktion",
      "berufsbezeichnung",
      "stellenbezeichnung",
    ],
  },
  {
    key: "company",
    label: "Company",
    required: false,
    aliases: [
      "organisation",
      "organization",
      "firma",
      "unternehmen",
      "company name",
    ],
  },
  {
    key: "trackName",
    label: "Track",
    required: false,
    aliases: ["track name", "tracks", "thementisch", "themen tisch"],
  },
  {
    key: "attendeeType",
    label: "Attendee type",
    required: false,
    aliases: [
      "type",
      "typ",
      "participant type",
      "teilnehmertyp",
      "teilnehmerart",
      "intern/extern",
      "internal/external",
    ],
  },
];

export type AttendeeMapping = Partial<Record<AttendeeColumnKey, string>>;

/** The obvious mapping for these headers: each column takes the first header matching its label, key or an alias. */
export function guessAttendeeMapping(headers: string[]): AttendeeMapping {
  const mapping: AttendeeMapping = {};
  const taken = new Set<string>();
  for (const column of ATTENDEE_COLUMNS) {
    const names = new Set(
      [column.label, column.key, ...column.aliases].map(squashHeader),
    );
    const match = headers.find(
      (header) => !taken.has(header) && names.has(squashHeader(header)),
    );
    if (match) {
      mapping[column.key] = match;
      taken.add(match);
    }
  }
  return mapping;
}

/** What is wrong with a mapping, or null when it can be sent. */
export function attendeeMappingProblem(
  mapping: AttendeeMapping,
): string | null {
  for (const column of ATTENDEE_COLUMNS) {
    if (column.required && !mapping[column.key])
      return `Map a column to ${column.label}.`;
  }
  const chosen = Object.values(mapping).filter(Boolean);
  if (new Set(chosen).size !== chosen.length)
    return "Each CSV column can only be mapped once.";
  return null;
}
